import Papa from "papaparse";

// Декодира съдържанието на CSV файл според реалния му encoding.
// Excel в България често записва CSV в Windows-1251 (ANSI); ако четем такъв
// файл като UTF-8, кирилицата се поврежда на "�". Опитваме строг UTF-8 и при
// неуспех декодираме като Windows-1251. (Изнесено от DataUpload.js — Г4 §2.)
export const decodeFileContent = (buffer) => {
    const bytes = new Uint8Array(buffer);
    if (bytes.length >= 3 && bytes[0] === 0xEF && bytes[1] === 0xBB && bytes[2] === 0xBF) {
        return new TextDecoder("utf-8").decode(bytes.subarray(3));
    }
    try {
        return new TextDecoder("utf-8", {fatal: true}).decode(bytes);
    } catch (e) {
        return new TextDecoder("windows-1251").decode(bytes);
    }
};

// Ж3: разделителят в шаблоните е „;" — българският Excel пише и очаква него.
// При четене разделителят се разпознава автоматично от Papa Parse, така че и
// „," работи; това важи и за двете секции за качване.
export const CSV_DELIMITER = ";";

// Ж3: когато разделителят е „;", българският Excel записва десетичните със
// ЗАПЕТАЯ (12,000). Всяко число от CSV минава оттук.
export const toNumber = (value) => Number(String(value ?? "").trim().replace(",", "."));

// Ж3: същото важи и за номера на имот — ако Excel го е форматирал като число,
// „36.149" се връща като „36,149" и масивът (частта преди точката) се чупи.
export const normalizeProperty = (value) => String(value ?? "").trim().replace(",", ".");

// Е21: сваля шаблонен CSV само с шапката. UTF-8 BOM (﻿), за да отвори Excel
// кирилицата коректно; CRLF за Windows-съвместимост.
export const downloadCsvTemplate = (headerLine, filename) => {
    const BOM = String.fromCharCode(0xFEFF);
    const blob = new Blob([BOM + headerLine + "\r\n"], {type: "text/csv;charset=utf-8;"});
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
};

// --- Л: масов внос от стари бази -------------------------------------------
// Шапките са ТОЧНО както ги пише старата система: „Взимащ" (не „Вземащ"),
// „ЕКТТ" (не „ЕКАТТЕ"), а в единия файл „Година " е с интервал накрая.
// Затова: сравнява се без регистър и без интервали, а за колоните, които са
// били изписвани по два начина, се приемат и двата.
// Р5: шапката на ПЪРВОНАЧАЛНИТЕ данни също стана българска. Английските имена
// остават като приети синоними — иначе всеки стар файл, който геодезистът вече
// има, щеше да спре да се качва. Шапката не е надпис, а договорът с файла.
const COL_ALIASES = {
    giving: ["Даващ"],
    receiving: ["Взимащ", "Вземащ"],
    target: ["За село"],
    amount: ["Сума"],
    user_name: ["Ползвател"],
    massif: ["Масив"],
    mpo: ["МПО Номер", "МПО номер", "МПО"],
    area: ["Площ"],
    year: ["Година", "year"],
    ekatte: ["ЕКАТТЕ", "ЕКТТ", "settlement_ekatte"],
    bullstat: ["Булстат", "bullstat"],
    company_name: ["Ползвател", "company_name"],
    declared_land: ["Декларирана", "declared_land"],
    unsuitable_land: ["Необработваема", "Негодна", "unsuitable_land"],
    suitable_land: ["Годна", "suitable_land"],
};

const canon = (s) => String(s ?? "").trim().toLowerCase().replace(/\s+/g, " ");

// Съпоставя реалните заглавия на файла към нашите имена. Връща {key: заглавие}
// за намерените и списък с липсващите.
const mapHeaders = (columns, keys) => {
    const found = {};
    const missing = [];
    keys.forEach(key => {
        const hit = columns.find(c => COL_ALIASES[key].some(a => canon(a) === canon(c)));
        if (hit) found[key] = hit; else missing.push(COL_ALIASES[key][0]);
    });
    return {found, missing};
};

const parseImportCsv = (text, keys) => {
    const parsed = Papa.parse(text, {
        header: true,
        skipEmptyLines: true,
        transformHeader: (h) => h.trim(),
        transform: (v) => v.trim(),
    });
    const data = parsed?.data || [];
    if (data.length === 0) return {error: "Файлът е празен или не може да се прочете."};

    const columns = Object.keys(data[0]);
    const {found, missing} = mapHeaders(columns, keys);
    if (missing.length > 0) {
        return {
            error: `Липсващи колони: ${missing.join(", ")}. `
                + `Във файла има: ${columns.join(", ")}.`
        };
    }

    // `row` е номерът на реда във файла (шапката е ред 1) — с него сървърът
    // посочва точно кой ред е проблемен.
    const rows = data.map((r, i) => {
        const out = {row: i + 2};
        const NUMERIC = ["amount", "area", "massif", "mpo", "year"];
        keys.forEach(k => {
            const raw = String(r[found[k]] ?? "").trim();
            // Празна клетка → null, а НЕ 0: `Number("")` е нула и празен масив
            // би влязъл като „масив 0", вместо да се оплаче.
            if (NUMERIC.includes(k)) out[k] = raw === "" ? null : toNumber(raw);
            else out[k] = raw;
        });
        return out;
    });
    return {rows};
};

// Файл 1 — доброволни замени: Даващ;Взимащ;За село;Сума;Година;ЕКТТ
export const DEALS_IMPORT_COLUMNS = ["Даващ", "Взимащ", "За село", "Сума", "Година", "ЕКТТ"];
export const parseDealsImportCsv = (text) =>
    parseImportCsv(text, ["giving", "receiving", "target", "amount", "year", "ekatte"]);

// Файл 2 — изчертани площи: Ползвател;Масив;МПО Номер;Площ;Година;ЕКТТ
export const DRAWINGS_IMPORT_COLUMNS = ["Ползвател", "Масив", "МПО Номер", "Площ", "Година", "ЕКТТ"];
export const parseDrawingsImportCsv = (text) =>
    parseImportCsv(text, ["user_name", "massif", "mpo", "area", "year", "ekatte"]);

// Р5 — първоначални данни (правно основание). Шапката вече е българска, но
// СТАРИТЕ файлове с английски имена продължават да се качват (виж COL_ALIASES).
export const INITIAL_IMPORT_COLUMNS = ["Булстат", "Ползвател", "Декларирана",
    "Необработваема", "Годна", "Година", "ЕКАТТЕ"];
const INITIAL_KEYS = ["bullstat", "company_name", "declared_land",
    "unsuitable_land", "suitable_land", "year", "ekatte"];

export const parseInitialImportCsv = (text) => {
    const резултат = parseImportCsv(text, INITIAL_KEYS);
    if (резултат.error) return резултат;

    // Останалата част от екрана работи с имената, с които се пише и в API-то —
    // затова тук се връщат те, независимо на какъв език е бил файлът.
    return {
        rows: резултат.rows.map(r => ({
            bullstat: r.bullstat,
            company_name: r.company_name,
            declared_land: r.declared_land,
            unsuitable_land: r.unsuitable_land,
            suitable_land: r.suitable_land,
            year: r.year,
            settlement_ekatte: r.ekatte,
        })),
    };
};

// Е5 — нов формат на файла. Шапка (точно тези думи):
// Класификация,Желание,Година,ЕКАТТЕ,Номер на имот,Площ,Булстат,Ползвател
export const DECL_COLUMNS = ["Класификация", "Желание", "Година", "ЕКАТТЕ",
    "Номер на имот", "Площ", "Булстат", "Ползвател"];
// „Класификация" = старото поле „Желание" → мапва се на `participates`.
const CLASSIF_YES = "За разпределяне";
const CLASSIF_NO = "Не участва в разпределянето";
// „Желание" = НОВО поле → съхранява се в `wish` (само за показ, Е5).
const WISH_REAL = "Обработка в реални граници";
const WISH_AGREEMENT = "За участие в споразумение";

// Парсва CSV с декларации (Е5). Връща {rows} или {error}.
// Papa Parse авт. разпознава разделителя (`;` или `,`); площта се нормализира
// с десетична запетая; номерът на имот НЕ се пипа (той е с точка).
export const parseDeclarationsCsv = (text) => {
    const parsed = Papa.parse(text, {
        header: true,
        skipEmptyLines: true,
        transformHeader: (h) => h.trim(),
        transform: (v) => v.trim(),
    });
    const data = parsed?.data || [];
    if (data.length === 0) return {error: "Файлът е празен или не може да се прочете."};

    const columns = Object.keys(data[0]);
    const missing = DECL_COLUMNS.filter(c => !columns.includes(c));
    if (missing.length > 0) {
        return {error: `Липсващи колони: ${missing.join(", ")}. Очаквани: ${DECL_COLUMNS.join(", ")}.`};
    }

    const rows = [];
    const errors = [];
    data.forEach((r, i) => {
        const classif = (r["Класификация"] || "").trim();
        let participates;
        if (classif === CLASSIF_YES) participates = true;
        else if (classif === CLASSIF_NO) participates = false;
        else {
            errors.push(`Ред ${i + 2}: непозната „Класификация" — „${classif}".`);
            return;
        }
        const wish = (r["Желание"] || "").trim();
        if (wish !== WISH_REAL && wish !== WISH_AGREEMENT) {
            errors.push(`Ред ${i + 2}: непознато „Желание" — „${wish}".`);
            return;
        }
        const area = toNumber(r["Площ"]);
        if (!Number.isFinite(area)) {
            errors.push(`Ред ${i + 2}: невалидна площ — „${r["Площ"]}".`);
            return;
        }
        const bullstat = String(r["Булстат"] ?? "").trim();
        if (!bullstat) {
            errors.push(`Ред ${i + 2}: липсва булстат.`);
            return;
        }
        const property_number = normalizeProperty(r["Номер на имот"]);
        if (!property_number) {
            errors.push(`Ред ${i + 2}: липсва номер на имот.`);
            return;
        }
        rows.push({
            property_number,
            area,
            participates,
            wish,
            // Ж2: булстатът остава ТЕКСТ. ОСЗ издава служебни номера с кирилица
            // („0802КВЙ040588"); Number(...) даваше NaN → null → неясна грешка.
            bullstat,
            declarator_name: (r["Ползвател"] || "").trim(),
            year: Number(r["Година"]),
            ekatte: (r["ЕКАТТЕ"] || "").trim(),
        });
    });

    if (errors.length > 0) return {error: errors.join(" ")};
    return {rows};
};
